import { ActionButton } from '@/components/common/ActionButton';
import { CARD_PADDED } from '@/components/common/surfaces';
import { SignIn, WarningCircle } from '@phosphor-icons/react';
import React, { type ReactNode, useEffect, useState } from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';

interface ProtectedAppProps {
  children: ReactNode;
}

/** The corner notice used while the OIDC library is mid-redirect; deliberately not a full screen. */
const STATUS_NOTE_CLASSES = 'fixed bottom-4 right-4 text-xs text-gray-400';

interface AuthGateProps {
  title: string;
  icon: ReactNode;
  actionLabel: string;
  onAction: () => void;
}

/**
 * The centred card the app shows instead of itself: signed out, or signed in and refused.
 *
 * One card rather than a bare heading on the page, so the screen has a focal point at the size
 * the rest of the app uses — and so the sign-in button is the only mark on it.
 */
const AuthGate: React.FC<AuthGateProps> = ({ title, icon, actionLabel, onAction }) => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
    <div className={`${CARD_PADDED} flex w-full max-w-[400px] flex-col items-center gap-4 text-center`}>
      <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">{icon}</span>
      <h1 className="text-lg font-semibold text-gray-900 [text-wrap:pretty]">{title}</h1>
      <ActionButton variant="primary" icon={<SignIn size={16} aria-hidden="true" />} onClick={onAction}>
        {actionLabel}
      </ActionButton>
    </div>
  </div>
);

export const ProtectedApp: React.FC<ProtectedAppProps> = (props) => {
  const { children } = props;
  const auth = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);

  useEffect(() => {
    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSignin) {
      auth.signinRedirect();
      setHasTriedSignin(true);
    }
  }, [auth, hasTriedSignin]);

  switch (auth.activeNavigator) {
    case 'signinSilent':
      return <p className={STATUS_NOTE_CLASSES}>Signing you in...</p>;
    case 'signoutRedirect':
      return <p className={STATUS_NOTE_CLASSES}>Signing you out...</p>;
  }

  return (
    <>
      {auth.error ? (
        <AuthGate
          title="Sign-in did not go through. Please try again."
          icon={<WarningCircle size={28} aria-hidden="true" />}
          actionLabel="Sign in"
          onAction={() => auth.signinRedirect()}
        />
      ) : auth.isLoading ? (
        <p className={STATUS_NOTE_CLASSES}>Authenticating...</p>
      ) : auth.isAuthenticated ? (
        children
      ) : (
        <AuthGate
          title="Welcome back. Sign in to continue."
          icon={<SignIn size={28} aria-hidden="true" />}
          actionLabel="Sign in"
          onAction={() => auth.signinRedirect()}
        />
      )}
    </>
  );
};
